import React, { useState, useEffect, useRef } from 'react';
import { prayersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { prayerWallScripture } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  HandHeart,
  Plus,
  Heart,
  MessageCircle,
  Send,
  User,
  Loader2,
  BookOpen,
  Archive,
  Trash2,
  MoreVertical,
  SmilePlus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import BottomNav from '../components/BottomNav';
import notificationSound from '../utils/notificationSound';
import { LinkifyText } from '../utils/linkify';

// Available reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '🙏', '😂', '🎉', '👏'];

const PrayerWallPage = () => {
  const { user, isAdmin } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPrayer, setNewPrayer] = useState('');
  const [commentText, setCommentText] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Check if user is pastor or admin
  const canManagePrayers = user?.role === 'pastor' || user?.role === 'admin';
  const prevPrayerCount = useRef(0);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    fetchPrayers();
    // Mark that user has visited the prayer wall
    prayersAPI.markVisited().catch(console.error);
    
    // Poll for new prayers every 30 seconds
    const pollInterval = setInterval(() => {
      fetchPrayers();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const fetchPrayers = async () => {
    try {
      const data = await prayersAPI.getAll();
      
      // Play sound if new prayers came in (not on first load)
      if (!isFirstLoad.current && data.length > prevPrayerCount.current) {
        notificationSound.playPrayerSound();
      }
      prevPrayerCount.current = data.length;
      isFirstLoad.current = false;
      
      setPrayers(data);
    } catch (error) {
      console.error('Error fetching prayers:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }) + ` at ${timeStr}`;
    }
  };

  const handleSubmitPrayer = async (e) => {
    e.preventDefault();
    if (!newPrayer.trim()) return;

    setSubmitting(true);
    try {
      const prayer = await prayersAPI.create(newPrayer);
      setPrayers([prayer, ...prayers]);
      setNewPrayer('');
      setShowAddDialog(false);
    } catch (error) {
      console.error('Error submitting prayer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePray = async (prayerId) => {
    try {
      await prayersAPI.pray(prayerId);
      setPrayers(prayers.map(p =>
        p.id === prayerId ? { ...p, prayerCount: p.prayerCount + 1 } : p
      ));
    } catch (error) {
      console.error('Error praying:', error);
    }
  };

  const handleAddComment = async (prayerId) => {
    const text = commentText[prayerId];
    if (!text?.trim()) return;

    try {
      const updatedPrayer = await prayersAPI.addComment(prayerId, text);
      setPrayers(prayers.map(p => p.id === prayerId ? updatedPrayer : p));
      setCommentText({ ...commentText, [prayerId]: '' });
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handlePrayerReaction = async (prayerId, emoji) => {
    try {
      const result = await prayersAPI.toggleReaction(prayerId, emoji);
      setPrayers(prayers.map(p => {
        if (p.id === prayerId) {
          return { ...p, reactions: result.reactions };
        }
        return p;
      }));
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleArchive = async (prayerId, isOwnPrayer) => {
    setActionLoading(prayerId);
    try {
      if (canManagePrayers && !isOwnPrayer) {
        // Admin/Pastor can globally archive
        await prayersAPI.archiveGlobal(prayerId);
      } else {
        // Members can archive for themselves
        await prayersAPI.archive(prayerId);
      }
      setPrayers(prayers.filter(p => p.id !== prayerId));
    } catch (error) {
      console.error('Error archiving prayer:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (prayerId) => {
    if (!window.confirm('Are you sure you want to permanently delete this prayer request?')) {
      return;
    }
    
    setActionLoading(prayerId);
    try {
      await prayersAPI.delete(prayerId);
      setPrayers(prayers.filter(p => p.id !== prayerId));
    } catch (error) {
      console.error('Error deleting prayer:', error);
      alert('Failed to delete prayer request. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <HandHeart className="w-6 h-6 text-tan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Prayer Community</h1>
            <p className="text-tan-400 text-sm italic">&ldquo;Pray without ceasing&rdquo; — 1 Thessalonians 5:17</p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Submit Prayer Request Button */}
        <Button
          onClick={() => setShowAddDialog(true)}
          className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-semibold text-base shadow-md"
        >
          <Plus className="w-5 h-5 mr-2" />
          Submit Prayer Request
        </Button>

        {/* Prayer List */}
        {prayers.map((prayer) => {
          const isOwnPrayer = prayer.userId === user?.id;
          
          return (
            <Card key={prayer.id} className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-5">
                {/* Prayer Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-navy-900" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-navy-900">{prayer.userName}</p>
                    <p className="text-xs text-gray-500">{formatDate(prayer.createdAt)}</p>
                  </div>
                  
                  {/* Actions Menu */}
                  {(canManagePrayers || isOwnPrayer) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          {actionLoading === prayer.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleArchive(prayer.id, isOwnPrayer)}>
                          <Archive className="w-4 h-4 mr-2" />
                          Archive
                        </DropdownMenuItem>
                        {canManagePrayers && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(prayer.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Prayer Content */}
                <p className="text-gray-700 leading-relaxed mb-4">
                  <LinkifyText>{prayer.request}</LinkifyText>
                </p>

                {/* Reactions */}
                <div className="flex items-center gap-2 mt-3">
                  {/* Existing Reactions */}
                  {prayer.reactions && Object.keys(prayer.reactions).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(prayer.reactions).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => handlePrayerReaction(prayer.id, emoji)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                            users.includes(user?.id)
                              ? 'bg-tan-200 text-tan-800 border border-tan-300'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span>{emoji}</span>
                          <span>{users.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Reaction Button */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="p-1.5 text-gray-400 hover:text-tan-600 hover:bg-gray-100 rounded-full transition-colors"
                        title="Add reaction"
                      >
                        <SmilePlus className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-2" side="top">
                      <div className="flex gap-1">
                        {REACTION_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => handlePrayerReaction(prayer.id, emoji)}
                            className="text-xl hover:scale-125 transition-transform p-1"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handlePray(prayer.id)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-tan-600 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    <span>Praying ({prayer.prayerCount})</span>
                  </button>
                  <button
                    onClick={() => setExpandedPrayer(expandedPrayer === prayer.id ? null : prayer.id)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy-900 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>Comments ({prayer.comments?.length || 0})</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedPrayer === prayer.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {prayer.comments?.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {prayer.comments.map((comment) => (
                          <div key={comment.id} className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-navy-900">{comment.userName}</span>
                              <span className="text-xs text-gray-400">{formatDate(comment.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-600">{comment.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Comment */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a comment..."
                        value={commentText[prayer.id] || ''}
                        onChange={(e) => setCommentText({ ...commentText, [prayer.id]: e.target.value })}
                        className="flex-1 h-10 bg-gray-50 border-gray-200 rounded-lg"
                      />
                      <Button
                        size="icon"
                        className="h-10 w-10 bg-navy-900 hover:bg-navy-800 text-white rounded-lg"
                        onClick={() => handleAddComment(prayer.id)}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {prayers.length === 0 && (
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-8 text-center">
              <HandHeart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No prayer requests yet</p>
              <p className="text-sm text-gray-400 mt-1">Be the first to share a prayer need</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Floating Action Button */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogTrigger asChild>
          <button
            className="fixed bottom-24 right-4 w-14 h-14 bg-navy-900 rounded-full shadow-lg flex items-center justify-center hover:bg-navy-800 transition-colors z-40"
          >
            <Plus className="w-6 h-6 text-white" />
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Submit Prayer Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitPrayer} className="space-y-4">
            <textarea
              placeholder="Share your prayer request with the church family..."
              className="w-full min-h-[120px] px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
              value={newPrayer}
              onChange={(e) => setNewPrayer(e.target.value)}
              required
            />
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-11 rounded-lg"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-lg"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default PrayerWallPage;
